import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pencil, RotateCcw, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface OverrideFieldProps {
    label: string;
    description?: string;
    isOverridden: boolean;
    onCustomize: () => void;
    onReset: () => void;
    renderBasePreview: () => ReactNode;
    children: ReactNode;
    customizeLabel?: string;
    resetLabel?: string;
    className?: string;
}

/**
 * Wraps a field on the In-app side of the agent builder with an inherit
 * default. When `isOverridden` is false the field shows a ghosted preview of
 * the website (base) value plus a "Customize for in-app" button. When true,
 * the editor renders normally plus a "Reset to website value" action.
 */
export default function OverrideField({
    label,
    description,
    isOverridden,
    onCustomize,
    onReset,
    renderBasePreview,
    children,
    customizeLabel = 'Customize for app mode',
    resetLabel = 'Reset to website mode value',
    className,
}: OverrideFieldProps) {
    return (
        <div className={cn('space-y-3 rounded-lg border bg-card p-4', className)}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0 basis-48">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">{label}</span>
                        {isOverridden ? (
                            <Badge
                                variant="outline"
                                className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                            >
                                Customized
                            </Badge>
                        ) : (
                            <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">
                                <Globe className="mr-1 h-3 w-3" />
                                Inherits from website mode
                            </Badge>
                        )}
                    </div>
                    {description && (
                        <p className="text-xs text-muted-foreground mt-1">{description}</p>
                    )}
                </div>
                {isOverridden ? (
                    <Button variant="ghost" size="sm" onClick={onReset} className="shrink-0">
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        {resetLabel}
                    </Button>
                ) : (
                    <Button variant="outline" size="sm" onClick={onCustomize} className="shrink-0">
                        <Pencil className="w-3.5 h-3.5 mr-1.5" />
                        {customizeLabel}
                    </Button>
                )}
            </div>

            {isOverridden ? (
                <div>{children}</div>
            ) : (
                <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
                    <div className="font-medium mb-1 text-muted-foreground/80">
                        Website mode value
                    </div>
                    {renderBasePreview()}
                </div>
            )}
        </div>
    );
}
